/**
 * Bar Intelligence on a chart: which bars feed it, what it draws, and what the
 * data panel shows. `docs/plans/bar-intelligence.md`.
 *
 * The arithmetic is `bar-intelligence.ts` (levels) and
 * `bar-intelligence-patterns.ts` (patterns); this file only decides the inputs
 * — the chart's own bars on a daily chart, the server's `dailyContext` on any
 * other — and turns the answer into derived drawings:
 *
 * Two indicators share it: **Bar Intelligence™** draws the record levels only,
 * **PA Levels** (PD, `qf-pd`) the reference levels too; the table is the same
 * for both (owner, 2026-10-12).
 *
 * - **Long-term** (MTD QTD YTD 52W ATH): a dotted line from the bar that set it to
 *   the current bar, and one label per stack, on the line, ending just left of
 *   the current bar. Hovering the label lists every level in the stack.
 * - **Short-term** (dH wH mH qH, PA Levels only): a short solid segment across the current period
 *   only, and a tag on the price axis — `dH 176.10`, `dH·wH 176.10` when they
 *   coincide.
 *
 * Highs take `negative` (resistance) and lows `positive` (support), the
 * trendlines' roles; a level within Near % turns `warn` and heavier; a crossed
 * one fades.
 */

import { insideSetupStats, outsideSetupStats, type SetupStats } from "./bar-setups";
import type { DerivedAnnotation } from "@qufolio/chart-engine";
import type { ChartGroup, Plot } from "@qufolio/model";
import type { GeometryPayload } from "@qufolio/transport";
import {
  barIntelParams,
  computeLevels,
  dayKey,
  isBarIntelAlias,
  isPaLevelsAlias,
  LONG_PERIODS,
  quarterKey,
  monthKey,
  stackHover,
  stackLevels,
  stackTag,
  stackText,
  weekKey,
  type BiBar,
  type BiCurrent,
  type BiLevel,
  type BiParams,
  type BiPeriod,
  type BiResult,
  type BiSeeds,
} from "./bar-intelligence";
import { patternRows, type ChartBars, type PatternRow } from "./bar-intelligence-patterns";

/** What the data panel's Bar Intelligence view renders, per chart group. */
export interface BarIntelSnapshot {
  symbol: string;
  params: BiParams;
  result: BiResult;
  patterns: PatternRow[];
  /** What a pattern's bar count is in: `d` on a daily chart, `w` weekly, `b` otherwise. */
  unit: string;
  /** The current bar — the chart's own last bar (any interval). */
  current: { t: number; h: number; l: number; c: number };
  /** The owner's inside bar setup over the chart's settled bars (`lib/bar-setups.ts`). */
  insideSetup: SetupStats;
  /** The owner's outside bar setup, likewise. */
  outsideSetup: SetupStats;
}

type Grain = "intraday" | "daily" | "multiDay" | "longer";

/** How a chart's interval reads for Bar Intelligence. Absent means daily. */
export function grainOf(frequency: string | undefined): Grain {
  const m = /^(\d*)\s*([a-z]+)$/i.exec((frequency ?? "1d").trim());
  if (!m) return "daily";
  const count = m[1] ? Number(m[1]) : 1;
  const unit = m[2]!.toLowerCase();
  if (unit === "s" || unit === "mi" || unit === "h") return "intraday";
  if (unit === "d") return count === 1 ? "daily" : "multiDay";
  if (unit === "w") return "multiDay";
  return "longer";
}

/** Which periods mean anything at this grain: a weekly bar has no yesterday (decided 2026-10-12). */
function periodsFor(grain: Grain): readonly BiPeriod[] {
  switch (grain) {
    case "intraday":
    case "daily":
      return ["d", "w", "m", "pq", ...LONG_PERIODS];
    case "multiDay":
      return ["m", "pq", ...LONG_PERIODS];
    case "longer":
      return LONG_PERIODS;
  }
}

/**
 * The visible Bar Intelligence or PA Levels plot on a pane, if any. PA Levels
 * wins when both are there: it draws everything the other does, and two sets of
 * the same lines would be one too many.
 */
export function barIntelPlot(plots: readonly Plot[] | undefined): Plot | undefined {
  const visible = plots?.filter((p) => p.type === "geometry" && !p.hidden && isBarIntelAlias(p.outputAlias));
  return visible?.find((p) => isPaLevelsAlias(p.outputAlias)) ?? visible?.[0];
}

const HIGH_TOKEN = "negative";
const LOW_TOKEN = "positive";
const NEAR_TOKEN = "warn";
const LONG_DASH = [2, 4];
const CROSSED_OPACITY = 0.45;
const LABEL_FONT = 12;
/** How many trailing bars stand in for "what the chart is showing" when stacking labels. */
const SPAN_BARS = 150;

/**
 * Everything Bar Intelligence contributes to one chart: the drawings for its
 * price pane and the panel's snapshot. `undefined` when the chart has no
 * Bar Intelligence plot, or not enough bars to measure.
 */
export function barIntelFor(
  group: ChartGroup,
  bars: ChartBars,
  geometry: GeometryPayload | null | undefined,
  /** Leading bars that are final; the default counts them all. */
  settled: number = bars.t.length,
): { annotations: DerivedAnnotation[]; snapshot: BarIntelSnapshot } | undefined {
  const price = group.panes.find((p) => p.role === "price");
  const plot = barIntelPlot(price?.plots);
  const n = bars.t.length;
  if (!price || !plot || n === 0) return undefined;
  const params = barIntelParams(plot.outputAlias);
  const grain = grainOf(group.frequency);
  const last = n - 1;

  // The daily bars every level is a fact about, and the seeds older than them.
  let daily: BiBar[];
  let seeds: BiSeeds | undefined;
  if (grain === "daily") {
    daily = [];
    for (let i = 0; i < last; i++) daily.push({ t: bars.t[i]!, h: bars.h[i]!, l: bars.l[i]!, c: bars.c[i]! });
  } else {
    const ctx = (geometry?.sets[plot.outputAlias!] ?? []).find((s) => s.kind === "dailyContext");
    if (!ctx?.daily) return undefined; // the server has not answered yet
    const d = ctx.daily;
    daily = d.t.map((t, i) => ({ t, h: d.h[i]!, l: d.l[i]!, c: d.c[i]! }));
    seeds = ctx.seeds;
  }

  const current: BiCurrent = { t: bars.t[last]!, h: bars.h[last]!, l: bars.l[last]!, c: bars.c[last]! };
  if (grain === "intraday") {
    // Today's extremes for the cross test and "new today" — a level crossed at
    // 10:00 stays crossed at 15:00 (Q16); the distance reads the last bar alone.
    const today = dayKey(current.t);
    let h = -Infinity;
    let l = Infinity;
    let cMax = -Infinity;
    let cMin = Infinity;
    for (let i = last; i >= 0 && dayKey(bars.t[i]!) === today; i--) {
      h = Math.max(h, bars.h[i]!);
      l = Math.min(l, bars.l[i]!);
      cMax = Math.max(cMax, bars.c[i]!);
      cMin = Math.min(cMin, bars.c[i]!);
    }
    current.today = { h, l, cMax, cMin };
  }

  const result = computeLevels(daily, current, params, { seeds, periods: periodsFor(grain) });
  const patterns = patternRows(geometry, price.plots, bars, params.measure);

  // The span of the bars on screen — what a label's height is a share of.
  let hi = -Infinity;
  let lo = Infinity;
  for (let i = Math.max(0, n - SPAN_BARS); i < n; i++) {
    hi = Math.max(hi, bars.h[i]!);
    lo = Math.min(lo, bars.l[i]!);
  }
  const span = Number.isFinite(hi - lo) ? hi - lo : 0;

  const drawn = result.levels.filter((l) => (l.family === "short" ? params.shortTerm : params.longTerm));
  const annotations = drawLevels(plot, drawn, bars, span);
  return {
    annotations,
    snapshot: {
      symbol: group.symbol,
      params,
      result,
      patterns,
      unit: grain === "daily" ? "d" : grain === "multiDay" ? "w" : "b",
      current: { t: current.t, h: current.h, l: current.l, c: current.c },
      insideSetup: insideSetupStats(bars, settled, params.entry),
      outsideSetup: outsideSetupStats(bars, settled),
    },
  };
}

function tokenFor(level: BiLevel): string {
  if (level.state === "near" || (level.family === "short" && level.state === "through")) return NEAR_TOKEN;
  return level.side === "high" ? HIGH_TOKEN : LOW_TOKEN;
}

const idFor = (plotId: string, part: string) => `bi|${plotId}|${part}`;

function drawLevels(plot: Plot, levels: readonly BiLevel[], bars: ChartBars, span: number): DerivedAnnotation[] {
  const out: DerivedAnnotation[] = [];
  const n = bars.t.length;
  const last = n - 1;
  const currentT = bars.t[last]!;
  const firstT = bars.t[0]!;
  // "Just left of the current bar" — the bar before it, so the text never sits
  // on the candle it is measuring.
  const labelT = n > 1 ? bars.t[last - 1]! : currentT;
  const base = plot.style?.thickness ?? 1;

  // Long-term: a dotted line per level, one label per stack.
  const long = levels.filter((l) => l.family === "long");
  for (const stack of stackLevels(long, span)) {
    for (const level of stack.all) {
      const near = level.state === "near";
      out.push({
        id: idFor(plot.id, `line|${level.key}`),
        type: "line",
        x1: Math.max(level.setT, firstT),
        y1: level.price,
        x2: currentT,
        y2: level.price,
        style: {
          stroke: tokenFor(level),
          thickness: near ? base + 1 : base,
          dash: LONG_DASH,
          ...(level.crossed ? { opacity: CROSSED_OPACITY } : {}),
        },
      });
    }
    out.push({
      id: idFor(plot.id, `label|${stack.top.key}`),
      type: "text",
      x1: labelT,
      y1: stack.top.price,
      text: stackText(stack),
      style: {
        fontColor: tokenFor(stack.top),
        fontSize: LABEL_FONT,
        ...(stack.top.crossed ? { opacity: CROSSED_OPACITY } : {}),
      },
      textAnchor: "right",
      hover: stackHover(stack),
    });
  }

  // Short-term: a segment across the current period, a tag on the axis.
  const short = levels.filter((l) => l.family === "short");
  for (const stack of stackLevels(short, span)) {
    for (const [i, level] of stack.all.entries()) {
      out.push({
        id: idFor(plot.id, `seg|${level.key}`),
        type: "line",
        x1: periodStart(level.period, bars),
        y1: level.price,
        x2: currentT,
        y2: level.price,
        style: { stroke: tokenFor(level), thickness: base + 1 },
        // Only the top of a stack is tagged, and it names them all.
        ...(i === 0 ? { axisLabel: stackTag(stack) } : {}),
      });
    }
  }
  return out;
}

/**
 * Where a short-term segment starts: the first chart bar of the current
 * period — today, this week, this month — or the bar before the current one
 * when the current one *is* the first, so the segment is never zero wide.
 */
function periodStart(period: BiPeriod, bars: ChartBars): number {
  const last = bars.t.length - 1;
  const key = period === "d" ? dayKey : period === "w" ? weekKey : period === "m" ? monthKey : quarterKey;
  const now = key(bars.t[last]!);
  let i = last;
  while (i > 0 && key(bars.t[i - 1]!) === now) i--;
  if (i === last && last > 0) i = last - 1;
  return bars.t[i]!;
}
